import { useState, useEffect, useCallback, useRef } from "react";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plane,
  PlaneLanding,
  PlaneTakeoff,
  AlertTriangle,
  CheckCircle,
  Activity,
  Clock,
  RefreshCw,
  TrendingUp,
  Wifi,
  WifiOff,
  Zap,
  Globe,
  MessageSquare,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { api, getApiBaseUrl } from "../lib/utils";
import { StatCard, useAnimatedNumber } from "../components/StatCard";
import { toast } from "sonner";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const MSG_COLORS = {
  FIDASM1: "#3b82f6",
  FIDASM2: "#ef4444",
  FIDTMATMS: "#22c55e",
  FIDTMAIRL: "#06b6d4",
  GASSNDALC: "#a855f7",
  FIDDLYDET: "#eab308",
  FIDDLTFLT: "#f97316",
  FIDUNDOFLT: "#64748b",
  FIDFCLNG1: "#ec4899",
  FIDFCLNG2: "#14b8a6",
  FIDADMRAD: "#8b5cf6",
  FIDAFS: "#0ea5e9",
  UNKNOWN: "#6b7280",
};

function getMsgColor(code) {
  return MSG_COLORS[code] || MSG_COLORS.UNKNOWN;
}

// Custom tooltip for chart
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
  return (
    <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg text-xs">
      <p className="font-medium text-foreground mb-1">{label}</p>
      {payload.filter(p => p.value > 0).map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 py-0.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-muted-foreground">{p.dataKey}:</span>
          <span className="font-medium">{p.value}</span>
        </div>
      ))}
      <div className="border-t border-border mt-1 pt-1 font-medium">Total: {total}</div>
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [integrationHealth, setIntegrationHealth] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [recentFlights, setRecentFlights] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartMsgCodes, setChartMsgCodes] = useState([]);
  const [topSenderIps, setTopSenderIps] = useState([]);
  const [chartRange, setChartRangeState] = useState("24h");
  const chartRangeRef = useRef("24h");
  const setChartRange = (r) => { chartRangeRef.current = r; setChartRangeState(r); };
  const [liveIds, setLiveIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [liveUpdating, setLiveUpdating] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const refreshTimerRef = useRef(null);
  const lastRefreshRef = useRef(0);

  const fetchStats = useCallback(async () => {
    try { const res = await api.get(`/api/dashboard/stats`); setStats(res.data); } catch (e) { console.error(e); }
  }, []);
  const fetchIntegrationHealth = useCallback(async () => {
    try { const res = await api.get(`/api/integration/health`); setIntegrationHealth(res.data); } catch (e) { console.error(e); }
  }, []);
  const fetchLogs = useCallback(async () => {
    try { const res = await api.get(`/api/integration/logs?limit=8`); setRecentLogs(res.data); } catch (e) { console.error(e); }
  }, []);
  const fetchFlights = useCallback(async () => {
    try { const res = await api.get(`/api/flights?limit=8`); setRecentFlights(res.data); } catch (e) { console.error(e); }
  }, []);
  const fetchChartData = useCallback(async () => {
    const range = chartRangeRef.current;
    try { const res = await api.get(`/api/dashboard/chart-data?range=${range}`); if (chartRangeRef.current !== range) return; setChartData(res.data.data || []); setChartMsgCodes(res.data.msg_codes || []); } catch (e) { console.error(e); }
  }, []);
  const fetchTopSenderIps = useCallback(async () => {
    try { const res = await api.get(`/api/dashboard/top-sender-ips?limit=5`); setTopSenderIps(res.data || []); } catch (e) { console.error(e); }
  }, []);

  const fetchAllData = useCallback(async (isRefresh = false, isLiveUpdate = false) => {
    if (isRefresh) setRefreshing(true);
    if (isLiveUpdate) setLiveUpdating(true);
    try {
      await Promise.all([fetchStats(), fetchIntegrationHealth(), fetchLogs(), fetchFlights(), fetchChartData(), fetchTopSenderIps()]);
    } catch (e) { console.error(e); } finally {
      setLoading(false); setRefreshing(false);
      if (isLiveUpdate) setTimeout(() => setLiveUpdating(false), 1000);
    }
  }, [fetchStats, fetchIntegrationHealth, fetchLogs, fetchFlights, fetchChartData, fetchTopSenderIps]);

  // Collapse WebSocket bursts: at most one full refresh every 5s, trailing-edge so the last message is never missed
  const scheduleLiveRefresh = useCallback(() => {
    if (refreshTimerRef.current) return;
    const wait = Math.max(0, 5000 - (Date.now() - lastRefreshRef.current));
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      lastRefreshRef.current = Date.now();
      fetchAllData(false, true);
    }, wait);
  }, [fetchAllData]);

  // Push the ingested message straight into the feeds so cards appear the instant MAHB sends them
  const pushLiveMessage = useCallback((d) => {
    if (!d || !d.id) return;
    const ts = d.received_at || new Date().toISOString();
    const logId = d.log_id || d.id;
    const logEntry = { id: logId, msg_code: d.msg_code || "UNKNOWN", status: "success", sender_ip: d.sender_ip, timestamp: ts,
      message: d.batch_count ? `Batch ingested: ${d.batch_count} flights` : `Ingested: ${d.flight_no || "Unknown"}` };
    setRecentLogs((prev) => prev.some((l) => l.id === logId) ? prev : [logEntry, ...prev].slice(0, 8));
    if (d.flight_no && (d.leg === "A" || d.leg === "D")) {
      const fl = { id: d.id, flight_no: d.flight_no, leg: d.leg, origin: d.origin, destination: d.destination, received_at: ts };
      setRecentFlights((prev) => prev.some((f) => f.id === d.id) ? prev : [fl, ...prev].slice(0, 8));
    }
    setLiveIds((prev) => { const n = new Set(prev); n.add(d.id); n.add(logId); return n; });
    setTimeout(() => setLiveIds((prev) => { const n = new Set(prev); n.delete(d.id); n.delete(logId); return n; }), 2500);
  }, []);

  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    const baseUrl = getApiBaseUrl();
    if (!baseUrl) return;
    const wsProtocol = baseUrl.startsWith("https") ? "wss" : "ws";
    const wsHost = baseUrl.replace(/^https?:\/\//, "");
    try {
      wsRef.current = new WebSocket(`${wsProtocol}://${wsHost}/api/ws/dashboard`);
      wsRef.current.onopen = () => { setWsConnected(true); reconnectAttempts.current = 0; const pi = setInterval(() => { if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send("ping"); }, 25000); wsRef.current.pingInterval = pi; };
      wsRef.current.onmessage = (event) => { try { const msg = JSON.parse(event.data); if (msg.type === "flight_update") { pushLiveMessage(msg.data); scheduleLiveRefresh(); } } catch (e) {} };
      wsRef.current.onclose = () => { setWsConnected(false); if (wsRef.current?.pingInterval) clearInterval(wsRef.current.pingInterval); const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000); reconnectAttempts.current += 1; reconnectTimeoutRef.current = setTimeout(() => connectWebSocket(), delay); };
      wsRef.current.onerror = () => {};
    } catch (e) {}
  }, [scheduleLiveRefresh, pushLiveMessage]);

  useEffect(() => {
    fetchAllData(); connectWebSocket();
    const poll = setInterval(() => { if (!wsConnected) fetchAllData(); }, 60000);
    return () => { clearInterval(poll); if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); if (wsRef.current) { if (wsRef.current.pingInterval) clearInterval(wsRef.current.pingInterval); wsRef.current.close(); } if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current); };
  }, []);

  useEffect(() => { fetchChartData(); }, [chartRange, fetchChartData]);
  useEffect(() => { if (!wsConnected && !reconnectTimeoutRef.current) connectWebSocket(); }, [wsConnected, connectWebSocket]);

  const formatDateTime = (isoString) => {
    if (!isoString) return "---";
    return new Date(isoString).toLocaleString("en-MY", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kuala_Lumpur" });
  };

  const getHealthBadge = (health) => {
    const s = { healthy: "bg-green-500/10 text-green-500 border-green-500/20", warning: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20", critical: "bg-red-500/10 text-red-500 border-red-500/20", no_data: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" };
    return <Badge variant="outline" className={s[health] || s.no_data}>{health === "no_data" ? "No Data" : health?.toUpperCase()}</Badge>;
  };

  const [msgsDisplay, msgsPulse] = useAnimatedNumber(integrationHealth?.total_lifetime_messages || 0);

  if (loading) return <Layout><div className="flex items-center justify-center h-[60vh]"><RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" /></div></Layout>;

  return (
    <Layout>
      <div className="w-full px-3 py-3 max-w-[1920px] mx-auto" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="font-heading text-xl md:text-2xl font-bold tracking-tight">DASHBOARD</h1>
            <p className="text-muted-foreground text-xs">Flight data overview and integration status</p>
          </div>
          <div className="flex items-center gap-1.5">
            {liveUpdating && <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20 animate-pulse text-[10px]"><Zap className="h-2.5 w-2.5 mr-0.5" />Updating</Badge>}
            {wsConnected ? <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20 text-[10px]"><Wifi className="h-2.5 w-2.5 mr-0.5" />Live</Badge> : <Badge variant="outline" className="bg-zinc-500/10 text-zinc-400 border-zinc-500/20 text-[10px]"><WifiOff className="h-2.5 w-2.5 mr-0.5" />Offline</Badge>}
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => fetchAllData(true)} disabled={refreshing}>
              <RefreshCw className={`h-3 w-3 mr-1 ${refreshing ? "animate-spin" : ""}`} />Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
          <StatCard icon={Plane} iconClass="text-primary" label="Total Flights" value={stats?.total_flights_today || 0} testId="stat-total-flights" />
          <StatCard icon={PlaneLanding} iconClass="text-green-500" label="Arrivals" value={stats?.total_arrivals || 0} testId="stat-arrivals" />
          <StatCard icon={PlaneTakeoff} iconClass="text-blue-500" label="Departures" value={stats?.total_departures || 0} testId="stat-departures" />
          <StatCard icon={AlertTriangle} iconClass="text-yellow-500" label="Delayed" value={stats?.delayed_flights || 0} testId="stat-delayed" />
          <Card className="bg-card/50 border-border">
            <CardContent className="p-2.5">
              <div className="flex items-center justify-between mb-0.5"><Activity className="h-4 w-4 text-cyan-500" />{getHealthBadge(stats?.integration_health)}</div>
              <p className="text-[10px] text-muted-foreground">Integration</p>
              <div className="flex items-center gap-1 mt-0.5"><MessageSquare className="h-2.5 w-2.5 text-muted-foreground" /><span className={`text-[10px] font-mono tabular-nums transition-colors duration-500 ${msgsPulse ? "text-green-500" : "text-muted-foreground"}`} data-testid="stat-lifetime-msgs">{msgsDisplay.toLocaleString()} msgs</span></div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border">
            <CardContent className="p-2.5">
              <div className="flex items-center gap-1 mb-1"><Globe className="h-3 w-3 text-purple-500" /><span className="text-[9px] font-medium text-muted-foreground uppercase">Top Sources</span></div>
              {topSenderIps.length === 0 ? <p className="text-[10px] text-muted-foreground">No data</p> : (
                <div className="space-y-0.5">
                  {topSenderIps.slice(0, 3).map((item, idx) => (
                    <div key={item.ip} className="flex items-center justify-between text-[10px]">
                      <span className="font-mono text-foreground">{idx + 1}. {item.ip}</span>
                      <Badge variant="outline" className="text-[8px] px-1 py-0 ml-1">{item.count}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Integration Activity Chart - Stacked Area */}
        <Card className="bg-card/50 border-border mb-3">
          <CardHeader className="py-2 px-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                <TrendingUp className="h-4 w-4" />Integration Activity
              </CardTitle>
              <Select value={chartRange} onValueChange={setChartRange}>
                <SelectTrigger className="w-[120px] h-7 text-[11px]" data-testid="chart-range-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1h">Last Hour</SelectItem>
                  <SelectItem value="24h">Last 24 Hours</SelectItem>
                  <SelectItem value="week">Last Week</SelectItem>
                  <SelectItem value="month">Last Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-2">
            <div className="h-[180px] w-full">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                    <defs>
                      {chartMsgCodes.map((code) => (
                        <linearGradient key={code} id={`grad-${code}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={getMsgColor(code)} stopOpacity={0.4} />
                          <stop offset="95%" stopColor={getMsgColor(code)} stopOpacity={0.05} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" opacity={0.3} />
                    <XAxis dataKey="time" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '4px' }} iconSize={8} />
                    {chartMsgCodes.map((code) => (
                      <Area
                        key={code}
                        type="monotone"
                        dataKey={code}
                        name={code}
                        stackId="1"
                        stroke={getMsgColor(code)}
                        fill={`url(#grad-${code})`}
                        strokeWidth={1.5}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <div className="text-center"><TrendingUp className="h-6 w-6 mx-auto mb-1 opacity-50" /><p className="text-xs">No data for this range</p></div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-3">
          <Card className="bg-card/50 border-border">
            <CardHeader className="py-2 px-3">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold"><Activity className="h-4 w-4" />Integration Activity</CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-2">
              <ScrollArea className="h-[220px]">
                {recentLogs.length === 0 ? <p className="text-muted-foreground text-center py-6 text-xs">No recent activity</p> : (
                  <div className="space-y-1.5">
                    {recentLogs.map((log) => (
                      <div key={log.id} data-testid={`activity-log-${log.id}`} className={`flex items-start gap-2 p-1.5 rounded transition-colors duration-700 ${liveIds.has(log.id) ? "bg-primary/15 ring-1 ring-primary/40 animate-in fade-in slide-in-from-top-2 duration-500" : "bg-muted/30"}`}>
                        {log.status === "success" ? <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 shrink-0" /> : <AlertTriangle className="h-3 w-3 text-red-500 mt-0.5 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="font-medium text-[11px]">{log.msg_code}</span>
                            <Badge variant="outline" className={`text-[9px] px-1 py-0 ${log.status === "success" ? "bg-green-500/10 text-green-500 border-green-500/20" : "bg-red-500/10 text-red-500 border-red-500/20"}`}>{log.status}</Badge>
                            {log.sender_ip && <span className="text-[9px] text-muted-foreground font-mono">{log.sender_ip}</span>}
                          </div>
                          <p className="text-[10px] text-muted-foreground truncate">{log.message}</p>
                          <p className="text-[9px] text-muted-foreground">{formatDateTime(log.timestamp)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border">
            <CardHeader className="py-2 px-3">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold"><Clock className="h-4 w-4" />Recent Flight Activity</CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-2">
              <ScrollArea className="h-[220px]">
                {recentFlights.length === 0 ? <p className="text-muted-foreground text-center py-6 text-xs">No recent flights</p> : (
                  <div className="space-y-1.5">
                    {recentFlights.map((flight) => (
                      <div key={flight.id} data-testid={`recent-flight-${flight.id}`} className={`flex items-center gap-2 p-1.5 rounded transition-colors duration-700 ${liveIds.has(flight.id) ? "bg-primary/15 ring-1 ring-primary/40 animate-in fade-in slide-in-from-top-2 duration-500" : "bg-muted/30"}`}>
                        {flight.leg === "A" ? <PlaneLanding className="h-3 w-3 text-green-500 shrink-0" /> : <PlaneTakeoff className="h-3 w-3 text-blue-500 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5"><span className="font-medium text-xs">{flight.flight_no || "---"}</span><span className="text-[10px] text-muted-foreground">{flight.origin || "---"} → {flight.destination || "---"}</span></div>
                          <p className="text-[9px] text-muted-foreground">{formatDateTime(flight.received_at)}</p>
                        </div>
                        <Badge variant="outline" className={`text-[9px] px-1 ${flight.leg === "A" ? "bg-green-500/10 text-green-500 border-green-500/20" : "bg-blue-500/10 text-blue-500 border-blue-500/20"}`}>{flight.leg === "A" ? "ARR" : "DEP"}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
